import { useState, useEffect, useCallback } from 'react';
import billingService from '../services/billing.service';
import { PricingResponse, ProductLine, StripePrice } from '../types/billing.types';

// Module-level cache to share pricing across all components
let pricingCache: PricingResponse | null = null;
let fetchPromise: Promise<PricingResponse> | null = null;

// Default fallback prices (in case API is unavailable)
// Pricing Strategy (Decoy Psychology):
// - COMPLY at $29 = "Good compliance value"
// - QUALITY at $29 = "Same price, just QA"
// - FULL at $39 = "Only $10 more for BOTH!" → Obvious choice
const defaultPricing: PricingResponse = {
  comply: {
    starter: { id: null, unit_amount: 2900, amount: 29, currency: 'usd', lookup_key: 'comply_starter', is_fallback: true },
    growth: { id: null, unit_amount: 9900, amount: 99, currency: 'usd', lookup_key: 'comply_growth', is_fallback: true },
  },
  quality: {
    starter: { id: null, unit_amount: 2900, amount: 29, currency: 'usd', lookup_key: 'quality_starter', is_fallback: true },
    growth: { id: null, unit_amount: 9900, amount: 99, currency: 'usd', lookup_key: 'quality_growth', is_fallback: true },
  },
  full: {
    starter: { id: null, unit_amount: 3900, amount: 39, currency: 'usd', lookup_key: 'full_starter', is_fallback: true },
    growth: { id: null, unit_amount: 12900, amount: 129, currency: 'usd', lookup_key: 'full_growth', is_fallback: true },
  },
  cache_initialized: false,
  prices_from_stripe: false,
};

interface UsePricingResult {
  pricing: PricingResponse;
  loading: boolean;
  error: Error | null;
  getPrice: (productLine: ProductLine, tier: 'starter' | 'growth') => StripePrice;
  formatPrice: (productLine: ProductLine, tier: 'starter' | 'growth') => string;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and cache Stripe pricing.
 *
 * Prices are fetched once and cached at the module level,
 * so all components share the same pricing data.
 *
 * @example
 * const { pricing, loading, formatPrice } = usePricing();
 *
 * // Get formatted price
 * const price = formatPrice('comply', 'starter'); // "$29"
 *
 * // Get raw price data
 * const priceData = getPrice('full', 'growth'); // { amount: 129, ... }
 */
export function usePricing(): UsePricingResult {
  const [pricing, setPricing] = useState<PricingResponse>(pricingCache || defaultPricing);
  const [loading, setLoading] = useState<boolean>(!pricingCache);
  const [error, setError] = useState<Error | null>(null);

  const fetchPricing = useCallback(async () => {
    // If already cached, use it
    if (pricingCache) {
      setPricing(pricingCache);
      setLoading(false);
      return;
    }

    // If a fetch is already in progress, wait for it
    if (fetchPromise) {
      try {
        const result = await fetchPromise;
        setPricing(result);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch pricing'));
        setLoading(false);
      }
      return;
    }

    // Start a new fetch
    setLoading(true);
    fetchPromise = billingService.getPricing();

    try {
      const result = await fetchPromise;
      pricingCache = result;
      setPricing(result);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch pricing'));
      // Keep using default pricing on error
    } finally {
      setLoading(false);
      fetchPromise = null;
    }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const getPrice = useCallback((productLine: ProductLine, tier: 'starter' | 'growth'): StripePrice => {
    return pricing[productLine][tier];
  }, [pricing]);

  const formatPrice = useCallback((productLine: ProductLine, tier: 'starter' | 'growth'): string => {
    const price = pricing[productLine][tier];
    return `$${Math.round(price.amount)}`;
  }, [pricing]);

  const refetch = useCallback(async () => {
    // Clear cache and refetch
    pricingCache = null;
    fetchPromise = null;
    await fetchPricing();
  }, [fetchPricing]);

  return {
    pricing,
    loading,
    error,
    getPrice,
    formatPrice,
    refetch,
  };
}

/**
 * Clear the pricing cache.
 * Useful for testing or when prices need to be refreshed.
 */
export function clearPricingCache(): void {
  pricingCache = null;
  fetchPromise = null;
}

export default usePricing;
